/**
 * 统一的版本检查工具
 * 支持两种检查方式：版本文件检查和文件修改时间检查
 */
const currentVersion = '2026.10.25.1500';
class UnifiedVersionChecker {
  constructor() {
    this.currentVersion = currentVersion;
    this.checkInterval = 3 * 60 * 1000; // 3分钟检查一次
    this.timer = null;
    this.isChecking = false;
    this.styleElement = null;
    this.notificationElement = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.lastCheckTime = localStorage.getItem('unifiedVersionChecker_lastCheckTime') || 0;
    this.checkMethod = 'auto'; // 'version-file', 'last-modified', 'auto'
  }

  /**
   * 启动版本检查
   */
  start() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    
    // 立即检查一次
    this.checkVersion();
    
    // 设置定时检查
    this.timer = setInterval(() => {
      this.checkVersion();
    }, this.checkInterval);
  }

  /**
   * 停止版本检查
   */
  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.cleanup();
  }

  /**
   * 清理资源
   */
  cleanup() {
    if (this.notificationElement && document.body.contains(this.notificationElement)) {
      document.body.removeChild(this.notificationElement);
      this.notificationElement = null;
    }
    if (this.styleElement && document.head.contains(this.styleElement)) {
      document.head.removeChild(this.styleElement);
      this.styleElement = null;
    }
  }

  /**
   * 检查版本更新
   */
  async checkVersion() {
    if (this.isChecking) return;
    
    this.isChecking = true;
    
    try {
      let hasUpdate = false;
      
      // 优先使用版本文件检查
      try {
        hasUpdate = await this.checkVersionFile();
      } catch (error) {
        console.log('版本文件检查失败，尝试文件修改时间检查:', error);
        // 如果版本文件检查失败，尝试文件修改时间检查
        try {
          hasUpdate = await this.checkLastModified();
        } catch (lastModifiedError) {
          console.log('文件修改时间检查也失败:', lastModifiedError);
          throw error; // 抛出原始错误
        }
      }
      
      if (hasUpdate) {
        this.showUpdateNotification();
      }
      
      // 重置重试计数
      this.retryCount = 0;
      this.lastCheckTime = Date.now();
      localStorage.setItem('unifiedVersionChecker_lastCheckTime', this.lastCheckTime);
      
    } catch (error) {
      console.log('版本检查失败:', error);
      this.retryCount++;
      
      // 如果还有重试次数，延迟重试
      if (this.retryCount < this.maxRetries) {
        setTimeout(() => {
          this.isChecking = false;
          this.checkVersion();
        }, 1000 * this.retryCount); // 递增延迟
        return;
      }
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * 检查版本文件
   */
  async checkVersionFile() {
    const response = await fetch('/version.json', {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    const serverVersion = data.version;
    return this.hasUpdate(this.currentVersion, serverVersion);
  }

  /**
   * 检查文件修改时间
   */
  async checkLastModified() {
    const response = await fetch(window.location.pathname, {
      method: 'HEAD',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const lastModifiedHeader = response.headers.get('last-modified');
    if (!lastModifiedHeader) {
      throw new Error('服务器未返回last-modified头');
    }
    
    const lastModified = new Date(lastModifiedHeader).getTime();
    const currentTime = Date.now();
    
    // 如果文件修改时间比上次检查时间新，说明有更新
    return lastModified > this.lastCheckTime;
  }

  /**
   * 比较版本号
   */
  hasUpdate(currentVersion, serverVersion) {
    try {
      const current = this.parseVersion(currentVersion);
      const server = this.parseVersion(serverVersion);
      console.log('------------serverVersion----------', serverVersion);
      console.log('-------------currentVersion---------', this.currentVersion);

      for (let i = 0; i < Math.max(current.length, server.length); i++) {
        const currentPart = current[i] || 0;
        const serverPart = server[i] || 0;
        if (serverPart > currentPart) {
          console.log('------------需要更新----------');
          return true;
        } else if (serverPart < currentPart) {
          console.log('------------不更新----------');
          return false;
        }
      }
      
      return false;
    } catch (error) {
      console.error('版本号比较失败:', error);
      return false;
    }
  }

  /**
   * 解析版本号
   */
  parseVersion(version) {
    if (!version || typeof version !== 'string') {
      throw new Error('无效的版本号格式');
    }
    return version.split('.').map(part => {
      const num = parseInt(part, 10);
      if (isNaN(num)) {
        throw new Error(`无效的版本号部分: ${part}`);
      }
      return num;
    });
  }

  /**
   * 显示更新通知
   */
  showUpdateNotification() {
    // 清理之前的通知
    this.cleanup();

    // 创建通知元素
    this.notificationElement = document.createElement('div');
    this.notificationElement.className = 'update-notification';
    this.notificationElement.innerHTML = `
      <div class="update-content">
        <div class="update-text">
          <div class="update-title">🔄发现新版本</div>
          <div class="update-desc">应用已更新，请刷新页面获取最新功能</div>
        </div>
        <div class="update-actions">
          <button class="update-btn refresh-btn">立即刷新</button>
          <button class="update-btn later-btn">稍后</button>
        </div>
      </div>
    `;

    // 添加样式（只添加一次）
    if (!this.styleElement) {
      this.styleElement = document.createElement('style');
      this.styleElement.textContent = `
        .update-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          background: #fff;
          border: 1px solid #e4e7ed;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 9999;
          max-width: 350px;
          animation: slideInRight 0.3s ease-out;
        }
        
        .update-content {
          padding: 16px;
          display: flex;
          gap: 12px;
          flex-direction: column;
        }
        
        .update-icon {
          font-size: 24px;
          flex-shrink: 0;
        }
        
        .update-text {
          flex: 1;
        }
        
        .update-title {
          font-weight: 600;
          color: #303133;
          margin-bottom: 4px;
        }
        
        .update-desc {
          font-size: 14px;
          color: #606266;
        }
        
        .update-actions {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }
        
        .update-btn {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .refresh-btn {
          background: #409eff;
          color: white;
        }
        
        .refresh-btn:hover {
          background: #337ecc;
        }
        
        .later-btn {
          background: #f5f7fa;
          color: #606266;
        }
        
        .later-btn:hover {
          background: #e4e7ed;
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .update-notification.hide {
          animation: slideOutRight 0.3s ease-in forwards;
        }
        
        @keyframes slideOutRight {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0;
            transform: translateX(100%);
          }
        }
      `;
      document.head.appendChild(this.styleElement);
    }

    document.body.appendChild(this.notificationElement);

    // 绑定事件
    const refreshBtn = this.notificationElement.querySelector('.refresh-btn');
    const laterBtn = this.notificationElement.querySelector('.later-btn');

    refreshBtn.addEventListener('click', () => {
      localStorage.setItem('unifiedVersionChecker_updateNotificationShown', 'true');
      this.hideNotification();
      setTimeout(() => {
        window.location.reload();
      }, 300);
    });

    laterBtn.addEventListener('click', () => {
      this.hideNotification();
    });
  }

  /**
   * 隐藏通知
   */
  hideNotification() {
    if (this.notificationElement) {
      this.notificationElement.classList.add('hide');
      setTimeout(() => {
        if (this.notificationElement && document.body.contains(this.notificationElement)) {
          document.body.removeChild(this.notificationElement);
          this.notificationElement = null;
        }
      }, 300);
    }
  }

  /**
   * 手动触发检查
   */
  manualCheck() {
    this.checkVersion();
  }

  /**
   * 设置检查方法
   */
  setCheckMethod(method) {
    this.checkMethod = method;
  }

  /**
   * 清除通知状态
   */
  clearNotificationState() {
    localStorage.removeItem('unifiedVersionChecker_updateNotificationShown');
  }
}

export default new UnifiedVersionChecker(); 